<template>
<div class="chapter">
<div class="inner_page">
<h2 class="chaptitleo"><a id="page_6"></a>Crochet know-how</h2>
<p class="h3"><span class="bold">UK and US crochet terminology</span></p>
<p class="noindent">The names for basic crochet stitches differ in the UK and the US. In all patterns, UK terms only are given. See opposite for a list of UK–US stitch conversion terms.</p>
<p class="h3"><span class="bold">Yarn</span></p>
<p class="noindent">With such amazing variety of yarns on the market we are truly spoiled for choice. However, for this book I concentrated more on colours and the effect of different yarns rather than the fibre content. I have used five basic yarns to create the flowers: pure wool double knitting or DK (8-ply/light worsted), cotton matt DK (8-ply/light worsted), mercerised cotton DK (8-ply/light worsted), matt cotton 5-ply (sport weight) and mohair 2-ply (lace weight) yarn. Yarn is such an amazing medium to work with that I urge you to experiment with different fibres to see what effects you can achieve. The yarn weights I have mentioned above are described below:</p>
<p class="noindentt"><span class="bold">DK</span> yarn (8-ply/light worsted) is a medium weight yarn, and is the most commonly used type. It can be used for most types of crocheting, from plain, lace and textured, producing medium weight garments.</p>
<p class="noindentt"><span class="bold">5-ply</span> (sport weight) yarn is slightly lighter than DK yarn and can be used for most types of crochet.</p>
<p class="noindentt"><span class="bold">1–3-ply</span> (lace weight) is a very fine yarn, used mostly for delicate openwork.</p>
<p class="h3"><span class="bold">Crochet hooks</span></p>
<p class="noindent">For most of the flowers I used a 3mm (UK 11, US C/D) crochet hook. I also used a 3.5mm (UK 9, US E/4) hook for one project (Blue, <span href="#/xhtml/10_Chapter04#page_12" title="Link target outside of Sample">page 12</span>) and a 4mm (UK 8, US G/6) for Rouge (<span href="#/xhtml/23_Chapter17#page_38" title="Link target outside of Sample">page 38</span>), as I didn’t want it to be as dense as the others. Try different hook sizes to achieve the size of finished flower that you want.</p>
<p class="indent">It is essential to work with a hook that is easy on your hands. Crochet hooks are made from aluminium, steel, plastic, bamboo and wood. I have always found wooden hooks wonderful to work with; however, it is best to experiment with different types to find one that suits you and offers comfort and control.</p>
<p class="h3"><span class="bold">Tension (gauge)</span></p>
<p class="noindent">I have not given a specific tension (gauge) for any of the projects, but the approximate completed size instead. It is not essential to achieve any particular tension on any of the items as they are accessories, so if they turn out a bit bigger or smaller it will not make much difference. However, the finished guide should provide a basic idea of what size the completed flower should be if you use yarn of a similar weight.</p>
<p class="h3"><span class="bold">Other materials</span></p>
<p class="h4"><span class="bold">Brooch backs</span></p>
<p class="noindent">Each flower has a sew-on brooch back attached to the reverse. Some of the large flowers will require two. Sew-on brooch backs are easily available and are recognisable by the holes on the back part of the finding. They come in several sizes, but I have only used medium and large ones in this book. The medium brooch back is 2.5cm (1in) long and the large one is 3cm (1¼in). They are very easily sewn onto the projects with a sewing needle and thread, or you could use the yarn used to make the flower and a tapestry needle. You may find it useful to back your flowers with felt before sewing on the brooch backs.</p>
<p class="h4"><span class="bold">Beads</span></p>
<p class="noindent">All beads used in this book are size 6 glass beads, approximately 4mm (3/16 in) in diameter. They are widely available from haberdashers and online bead stores.</p>
<p class="h4"><span class="bold">Buttons</span></p>
<p class="noindent">I used ceramic buttons in different sizes as I just loved the design, but any buttons are fine to use; the exact size of each button is given in the materials list.</p>
<div class="img_container">
<img alt="image" src="images/f0006-01.jpg"/>
</div>
<div class="org">
<p class="h3"><a id="page_7"></a><span class="bold">Blocking</span></p>
<p class="noindent">The blocking of your finished projects is vital; it will take your flowers from beautiful to stunning. It will uncurl your petals, enhance lace work and even out your work. However, not every flower in this book requires blocking. For the ones that do, the blocking method is recommended in the making up section.</p>
<p class="indent">Blocking should be done before you put all the pieces together. I have found two methods very successful when blocking small projects.</p>
<p class="h4"><span class="bold">Steam blocking</span></p>
<p class="noindent">You can use steam from an iron to block your flowers. Place the dry item on a padded surface and pin it using rust-resistant pins. Use the iron to apply steam to the item. Hold the iron about an inch above the project, moving slowly to make sure the steam penetrates the fabric. Do not apply the iron directly to the item. It will crush the fabric, and may harm fibres.</p>
<p class="h4"><span class="bold">Spray blocking</span></p>
<p class="noindent">With right side up, pin the item to shape on a padded surface placed away from direct sun or heat. Using a spray bottle filled with cool tap water, spritz a fine, even mist over the piece. Use your hands to gently pat the moisture into the project, then allow it to dry naturally. I have also found it very useful to gently spray the project while blocking with spray-on starch, just to add a bit of stiffness.</p>
<p class="h3"><span class="bold">Changing yarn colour</span></p>
<p class="noindent">Changing your yarn colour is very easy – simply follow the steps below.</p>
<p class="noindentt">When changing colour at the end of a row:</p>
<p class="noindent"><span class="bold">1</span> Work the last stitch (any stitch) to the last two loops on the hook.</p>
<p class="noindent"><span class="bold">2</span> Place the new yarn on the hook and pull the new colour through the two loops on the hook.</p>
<p class="noindent"><span class="bold">3</span> Pull the old and new yarn tails to tighten the loop on the hook. Continue working with the new colour.</p>
<p class="noindentt">When changing colour at the end of a round:</p>
<p class="noindent"><span class="bold">1</span> Insert the hook into the stitch or chain you will slip stitch into to close the round.</p>
<p class="noindent"><span class="bold">2</span> Place the new yarn on the hook and finish the slip stitch with the new colour.</p>
<p class="noindent"><span class="bold">3</span> Pull the old and new yarn tails to tighten the loop on the hook. Continue working with the new colour.</p>
<p class="center"><span class="bold">See <span href="#/xhtml/28_Chapter22#page_48" title="Link target outside of Sample">page 48</span> for a list of the actual yarns used in this book.</span></p>
<div class="img_container">
<img alt="image" src="images/f0007-01.jpg"/>
</div>
<div class="box1">
<p class="boxh"><span class="bold">Crochet abbreviations</span></p>
<p class="boxn1">The abbreviations listed below are the most frequently used terms in the book. Any special abbreviations in a crochet pattern are explained on the relevant project page.</p>
<table class="table">
<tbody><tr>
<td class="tab"><span class="bold">UK</span></td>
<td class="tab"><span class="bold">US</span></td>
</tr>
<tr>
<td class="tab"><span class="bold">blo</span> work into back loop only</td>
<td class="tab"><span class="bold">blo</span> work into back loop only</td>
</tr>
<tr>
<td class="tab"><span class="bold">dc</span> (double crochet)</td>
<td class="tab"><span class="bold">sc</span> (single crochet)</td>
</tr>
<tr>
<td class="tab"><span class="bold">flo</span> work into front loop only</td>
<td class="tab"><span class="bold">flo</span> work into front loop only</td>
</tr>
<tr>
<td class="tab"><span class="bold">htr</span> (half treble crochet)</td>
<td class="tab"><span class="bold">hdc</span> (half double crochet)</td>
</tr>
<tr>
<td class="tab"><span class="bold">tr</span> (treble crochet)</td>
<td class="tab"><span class="bold">dc</span> (double crochet)</td>
</tr>
<tr>
<td class="tab"><span class="bold">dtr</span> (double treble)</td>
<td class="tab"><span class="bold">tr</span> (treble)</td>
</tr>
<tr>
<td class="tab"><span class="bold">trtr</span> (triple treble)</td>
<td class="tab"><span class="bold">dtr</span> (double treble)</td>
</tr>
<tr>
<td class="tab"><span class="bold">bhtr</span> (beaded half treble crochet)</td>
<td class="tab"><span class="bold">bhdc</span> (beaded half double crochet)</td>
</tr>
<tr>
<td class="tab"><span class="bold">btr</span> (beaded treble)</td>
<td class="tab"><span class="bold">bdc</span> (beaded double crochet)</td>
</tr>
<tr>
<td class="tab"><span class="bold">bdtr</span> (beaded double treble)</td>
<td class="tab"><span class="bold">btr</span> (beaded treble crochet)</td>
</tr>
<tr>
<td class="tab"><span class="bold">btrtr</span> (beaded triple treble)</td>
<td class="tab"><span class="bold">bdtr</span> (beaded double treble)</td>
</tr>
<tr>
<td class="tab"><span class="bold">bpdc</span> (back post double crochet)</td>
<td class="tab"><span class="bold">bpsc</span> (back post single crochet)</td>
</tr>
<tr>
<td class="tab"><span class="bold">dtr3tog</span> (double treble 3 together)</td>
<td class="tab"><span class="bold">tr3tog</span> (treble 3 together)</td>
</tr>
<tr>
<td class="tab"><span class="bold">fpdc</span> (front post double crochet)</td>
<td class="tab"><span class="bold">fpsc</span> (front post single crochet)</td>
</tr>
<tr>
<td class="tab"><span class="bold">fptr</span> (front post treble crochet)</td>
<td class="tab"><span class="bold">fpdc</span> (front post double crochet)</td>
</tr>
<tr>
<td class="tab"><span class="bold">long tr</span> (long treble crochet)</td>
<td class="tab"><span class="bold">long dc</span> (long double crochet)</td>
</tr>
<tr>
<td class="tab"><span class="bold">miss</span></td>
<td class="tab"><span class="bold">skip</span></td>
</tr>
<tr>
<td class="tab"><span class="bold">reverse double crochet</span> (also known as crab stitch)</td>
<td class="tab"><span class="bold">reverse single crochet</span></td>
</tr>
<tr>
<td class="tab"><span class="bold">sdc</span> (spike double crochet)</td>
<td class="tab"><span class="bold">ssc</span> (spike single crochet)</td>
</tr>
<tr>
<td class="tab"><span class="bold">tension</span></td>
<td class="tab">gauge</td>
</tr>
<tr>
<td class="tab"><span class="bold">yrh</span> yarn round hook</td>
<td class="tab"><span class="bold">yrh</span> yarn round hook</td>
</tr>
</tbody></table>
</div>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// 07_Chapter01.vue?d77e13f8